'use client';

import React from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faEraser, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { PulseButton, Dialog } from 'src/components';

interface ClearGeographyDialogProps {
  /** Controls the visibility of the dialog */
  open: boolean;
  /** Callback function called when the dialog should close */
  onClose: () => void;
  /** Callback function called when user confirms clearing */
  onConfirm: () => void;
}

const ClearGeographyDialog = ({
  open,
  onClose,
  onConfirm,
}: ClearGeographyDialogProps): React.ReactElement => {
  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  return (
    <Dialog
      isOpen={open}
      onClose={onClose}
      aria-labelledby="clear-geography-dialog-title"
      data-testid="clear-geography-dialog"
    >
      <Dialog.Header>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-red-50">
              <Icon icon={faEraser} className="text-red-500" />
            </div>
            <div
              className="text-lg font-medium"
              id="clear-geography-dialog-title"
            >
              Clear All Geographies
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-neutral-500 hover:text-neutral-700 transition-colors"
            aria-label="Close dialog"
            data-testid="clear-geography-dialog-close"
          >
            <Icon icon={faXmark} />
          </button>
        </div>
      </Dialog.Header>
      <Dialog.Content>
        <div
          className="text-sm text-neutral-700 p-4"
          role="alert"
          data-testid="clear-geography-dialog-message"
        >
          Are you sure you want to remove all territories, regions, and states
          from the analysis?
        </div>
      </Dialog.Content>
      <Dialog.Actions>
        <PulseButton
          variant="outline"
          color="neutral"
          onClick={onClose}
          aria-label="Cancel clearing geographies"
          className="w-full rounded-full"
          data-testid="clear-geography-dialog-cancel"
        >
          Cancel
        </PulseButton>
        <PulseButton
          variant="solid"
          color="danger"
          onClick={handleConfirm}
          aria-label="Confirm clearing geographies"
          className="w-full rounded-full"
          data-testid="clear-geography-dialog-confirm"
        >
          Clear All
        </PulseButton>
      </Dialog.Actions>
    </Dialog>
  );
};

export default ClearGeographyDialog;
